import React, { FC, useCallback, useEffect, useMemo, useState } from "react";

import { faPalette } from "@fortawesome/pro-solid-svg-icons/faPalette";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import randomColor from "randomcolor";
import { defineMessages, useIntl } from "react-intl";

import AnyColor, { HexColor } from "@mapmycustomers/shared/types/AnyColor";
import { ColorItemType } from "@mapmycustomers/shared/types/map/PinLegend";
import { SelectField } from "@mapmycustomers/ui";

import {
  DEFAULT_BUCKETS_COUNT,
  DEFAULT_NO_VALUE_COLOR,
} from "@app/scene/map/components/pinLegends/utils/consts";
import {
  getThemeCategoryDisplayName,
  ThemeCategory,
  ThemeDescriptor,
  ThemeName,
  themes,
} from "@app/scene/map/components/pinLegends/utils/themes";
import useAnalytics from "@app/util/contexts/useAnalytics";

import ColorBar from "./ColorBar";
import styles from "./ColorThemeField.module.scss";
import CustomColors from "./CustomColors";

const messages = defineMessages({
  customThemeName: {
    id: "map.colorization.createEditSidebar.styleEditor.theme.color.customTheme.name",
    defaultMessage: "Custom",
    description: "Custom theme Label in the style editor in Pin Legends drawer",
  },
  label: {
    id: "map.colorization.createEditSidebar.styleEditor.theme.color.label",
    defaultMessage: "Color Theme",
    description: "Color Theme Label in the style editor in Pin Legends drawer",
  },
});

export interface ColorThemeFieldProps {
  colors: ColorItemType[];
  defaultColor: AnyColor;
  flip?: boolean;
  numberOfBuckets: number;
  onChange?: (colors: ColorItemType[]) => void;
  onDefaultColorChange?: (color: AnyColor) => void;
  onFlipChange?: (flip: boolean) => void;
}

const ColorThemeField: FC<ColorThemeFieldProps> = ({
  colors,
  defaultColor,
  flip,
  numberOfBuckets,
  onChange,
  onDefaultColorChange,
  onFlipChange,
}) => {
  const analytics = useAnalytics();
  const intl = useIntl();

  const [theme, setTheme] = useState<ThemeDescriptor>(() => {
    const customTheme = themes.find((theme) => theme.category === ThemeCategory.CUSTOM)!;
    if (numberOfBuckets !== colors.length || numberOfBuckets === 0) {
      return customTheme;
    }

    return (
      themes.find((theme) => {
        const colorFn = theme.getColorFn(numberOfBuckets);
        const foundMatch = colors.every(
          (item, index) =>
            item.color === colorFn(index) || item.color === colorFn(colors.length - index - 1)
        );
        if (foundMatch && colors[0].color !== colorFn(0)) {
          onFlipChange?.(true);
        }
        return foundMatch;
      }) ?? customTheme
    );
  });

  const handleThemeNameChange = useCallback(
    (name: ThemeName) => {
      const theme = themes.find((theme) => theme.name === name);
      if (theme) {
        setTheme(theme);
        analytics.clicked(["Theme"], { theme: name });
      }
    },
    [analytics]
  );

  // sync shapes array to match new number of buckets and theme
  useEffect(
    () => {
      if (theme.category === ThemeCategory.CUSTOM) {
        if (colors.length > numberOfBuckets) {
          onChange?.(colors.toSpliced(numberOfBuckets));
        } else if (colors.length < numberOfBuckets) {
          const result = [...colors];
          while (result.length < numberOfBuckets) {
            result.push({
              color: randomColor({
                format: "hex",
                luminosity: "dark",
                seed: (Date.now() * 0xdeadbeef) & 0xffffffff,
              }) as HexColor,
            }); // default random color
          }
          onChange?.(result);
        }
        return;
      }

      if (!numberOfBuckets || !onChange) {
        return;
      }

      const colorFn = theme.getColorFn(numberOfBuckets);
      const newColors = Array.from({ length: numberOfBuckets }).map(
        (_, index) => ({ color: colorFn(index) } as ColorItemType)
      );
      if (flip) {
        newColors.reverse();
      }
      onChange(newColors);
      onDefaultColorChange?.(DEFAULT_NO_VALUE_COLOR);
    },
    // We don't need to depend on colors array, we'll only check it when numberOfBuckets changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [numberOfBuckets, onChange, onDefaultColorChange, theme, flip]
  );

  const options = useMemo(() => {
    const themesByCategory = themes.reduce((result, theme) => {
      if (!(theme.category in result)) {
        result[theme.category] = [];
      }
      result[theme.category].push(theme);
      return result;
    }, {} as Record<ThemeCategory, ThemeDescriptor[]>);

    return (Object.values(ThemeCategory) as ThemeCategory[])
      .filter((category) => category in themesByCategory)
      .map((category) => ({
        label: getThemeCategoryDisplayName(intl, category),
        options: themesByCategory[category].map((theme) => ({
          label:
            theme.category !== ThemeCategory.CUSTOM ? (
              <ColorBar
                // use a default number of buckets if current number of buckets is 0
                // so that we can show at least some colors
                colorFn={theme.getColorFn(numberOfBuckets || DEFAULT_BUCKETS_COUNT)}
                count={numberOfBuckets || DEFAULT_BUCKETS_COUNT}
                flip={flip}
              />
            ) : (
              intl.formatMessage(messages.customThemeName)
            ),
          value: theme.name,
        })),
      }));
  }, [intl, numberOfBuckets, flip]);

  return (
    <div className={styles.container}>
      <span className={styles.label}>{intl.formatMessage(messages.label)}</span>
      <SelectField<ThemeName>
        className={styles.select}
        onChange={handleThemeNameChange}
        options={options}
        popupClassName={styles.selectDropdown}
        prefixIcon={<FontAwesomeIcon icon={faPalette} />}
        value={theme.name as ThemeName}
      />
      {theme.category === ThemeCategory.CUSTOM && (
        <CustomColors
          colors={colors}
          defaultColor={defaultColor}
          onChange={onChange}
          onDefaultColorChange={onDefaultColorChange}
        />
      )}
    </div>
  );
};

export default ColorThemeField;
